import React, { useState, useMemo } from 'react';
import { uploadAndProcessImage } from '../../imageUploadUtils';

// Card Types tab component
const CardTypesTab = ({ cardTypes, onEditCardType, onDeleteCardType, onRowClick, onAdd }) => {
  return (
    <div>
      <h2>Card Types</h2>
      <div style={{ marginBottom: "20px", display: 'flex', gap: '10px', alignItems: 'center' }}>
        <button className="small-action-btn add" onClick={() => onAdd && onAdd()}>
          Add Card Type
        </button>
      </div>
      <table>
        <thead>
          <tr>
            <th>ID</th>
            <th>Symbol</th>
            <th>Default Background</th>
            <th>Translations</th>
            <th>Actions</th>
          </tr>
        </thead>
        <tbody>
          {cardTypes.map((type) => (
            <tr key={type.type_id} onClick={() => onRowClick && onRowClick('cardTypes', type)} style={{ cursor: 'pointer' }}>
              <td>{type.type_id}</td>
              <td>
                {type.symbol_path ? (
                  <img src={type.symbol_path} alt="Symbol" style={{ width: 48, height: 48, objectFit: 'contain' }} title={type.symbol_path} />
                ) : (
                  <span style={{ color: '#aaa' }}>No symbol</span>
                )}
              </td>
              <td>
                {type.default_background ? (
                  <img src={type.default_background} alt="Background" style={{ width: 100, height: 100, objectFit: 'cover' }} title={type.default_background} />
                ) : (
                  <span style={{ color: '#aaa' }}>No image</span>
                )}
              </td>
              <td>
                {type.translations &&
                  Object.entries(type.translations).map(([lang, translation]) => (
                    <div key={lang}>
                      <b>{lang}:</b> {typeof translation === 'object' ? translation.name : translation}
                    </div>
                  ))}
              </td>
              <td>
                <button className="small-action-btn edit" onClick={(e) => { e.stopPropagation(); onEditCardType(type); }}>Edit</button>
                <button className="small-action-btn delete" onClick={(e) => { e.stopPropagation(); onDeleteCardType(type.type_id); }}>Delete</button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

// Add Card Type Form
export function AddCardTypeForm({ onAdd, onCancel, languages, symbols }) {
  const [defaultBackground, setDefaultBackground] = useState(null);
  const [symbolId, setSymbolId] = useState("");
  const [translations, setTranslations] = useState({});
  const [uploading, setUploading] = useState(false);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [newBackgroundFile, setNewBackgroundFile] = useState(null);

  // Memoize preview URL so it only updates when the file or background changes
  const memoPreviewUrl = useMemo(() => {
    if (defaultBackground instanceof File) return URL.createObjectURL(defaultBackground);
    if (defaultBackground) return defaultBackground;
    return null;
  }, [defaultBackground]);

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    setDefaultBackground(file);

    if (file) {
      const reader = new FileReader();
      reader.onload = (e) => setPreviewUrl(e.target.result);
      reader.readAsDataURL(file);
    } else {
      setPreviewUrl(null);
    }
  };

  const handleTranslationChange = (langCode, field, value) => {
    setTranslations(prev => ({
      ...prev,
      [langCode]: {
        ...prev[langCode],
        [field]: value,
      }
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!defaultBackground) {
      alert("Default background is required");
      return;
    }

    // Check if at least one language has a name
    const hasAnyTranslation = Object.values(translations).some(trans => trans && trans.name);
    if (!hasAnyTranslation) {
      alert("At least one language must have a name");
      return;
    }

    setUploading(true);
    try {
      let backgroundPath = defaultBackground;

      // If defaultBackground is a File object, upload it first
      if (defaultBackground instanceof File) {
        backgroundPath = await uploadAndProcessImage(defaultBackground, '/api/admin/card-types/upload-background');
      }

      await onAdd({
        default_background: backgroundPath,
        symbol_id: symbolId || null,
        translations: translations
      });
    } catch (err) {
      alert(err.message || "Failed to add card type");
    } finally {
      setUploading(false);
    }
  };

  return (
    <div style={{ width: '100%' }}>
      <form onSubmit={handleSubmit} style={{ marginBottom: "20px", padding: "20px", border: "1px solid #ccc", borderRadius: "20px" }}>
        <h3>Add Card Type</h3>

        <div style={{ marginBottom: "10px" }}>
          <label>Default Background (PNG):</label><br />
          <input
            type="file"
            accept="image/*"
            onChange={handleFileChange}
            style={{ marginLeft: "10px" }}
            required
          />
          {defaultBackground && (
            <div style={{ marginTop: "10px", marginLeft: "10px" }}>
              <img
                src={memoPreviewUrl}
                alt="Background Preview"
                style={{ width: 100, height: 100, objectFit: 'cover' }}
              />
            </div>
          )}
        </div>

        <div style={{ marginBottom: "10px" }}>
          <label>Symbol:</label><br />
          <select
            value={symbolId}
            onChange={(e) => setSymbolId(e.target.value)}
            style={{ width: "400px", marginLeft: "10px" }}
          >
            <option value="">No symbol</option>
            {symbols.filter(symbol => symbol.symbol_type === "Card type").sort((a, b) => ((a.translations?.en?.name || a.translations?.en) || `Symbol ${a.symbol_id}`).localeCompare((b.translations?.en?.name || b.translations?.en) || `Symbol ${b.symbol_id}`)).map(symbol => (
              <option key={symbol.symbol_id} value={symbol.symbol_id}>
                {(symbol.translations?.en?.name || symbol.translations?.en) || `Symbol ${symbol.symbol_id}`} ({symbol.symbol_type})
              </option>
            ))}
          </select>
        </div>

        <div style={{ marginBottom: "10px" }}>
          <label>Translations:</label>
          <div style={{ fontSize: "12px", color: "#666", marginBottom: "10px" }}>
            Note: If English is not provided, the system will automatically translate from the first available language to English, then to all other languages.
          </div>
          {languages.map(lang => (
            <div key={lang.code} style={{ marginTop: "10px" }}>
              <h4>{lang.name} ({lang.code})</h4>
              <div style={{ marginLeft: "20px" }}>
                <div style={{ marginBottom: "5px" }}>
                  <label>Name:</label>
                  <input
                    type="text"
                    value={translations[lang.code]?.name || ""}
                    onChange={(e) => handleTranslationChange(lang.code, "name", e.target.value)}
                    style={{ width: "400px", marginLeft: "10px" }}
                    placeholder="Optional (at least one language required)"
                  />
                </div>
              </div>
            </div>
          ))}
        </div>

        <button type="submit" disabled={uploading}>Add Card Type</button>
        <button type="button" onClick={onCancel} style={{ marginLeft: "10px" }}>Cancel</button>
      </form>
    </div>
  );
}

// Edit Card Type Form
export function EditCardTypeForm({ cardType, onSave, onCancel, languages, symbols }) {
  const [defaultBackground, setDefaultBackground] = useState(cardType.default_background);
  const [symbolId, setSymbolId] = useState(cardType.symbol_id || "");
  const [translations, setTranslations] = useState(cardType.translations || {});
  const [uploading, setUploading] = useState(false);
  const [newBackgroundFile, setNewBackgroundFile] = useState(null);

  // Memoize preview URL so it only updates when the file or background changes
  const previewUrl = useMemo(() => {
    if (newBackgroundFile) return URL.createObjectURL(newBackgroundFile);
    return defaultBackground;
  }, [newBackgroundFile, defaultBackground]);

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Check if at least one language has a name
    const hasAnyTranslation = Object.values(translations).some(trans => trans && trans.name);
    if (!hasAnyTranslation) {
      alert("At least one language must have a name");
      return;
    }

    setUploading(true);
    try {
      let backgroundPath = defaultBackground;
      if (newBackgroundFile) {
        backgroundPath = await uploadAndProcessImage(newBackgroundFile, '/api/admin/card-types/upload-background');
      }

      await onSave({
        default_background: backgroundPath,
        symbol_id: symbolId || null,
        translations: translations
      });
    } catch (err) {
      alert(err.message || "Failed to update card type");
    } finally {
      setUploading(false);
    }
  };

  return (
    <div style={{ width: '100%' }}>
      <form onSubmit={handleSubmit} style={{ marginBottom: "20px", padding: "20px", border: "1px solid #ccc", borderRadius: "20px" }}>
        <h3>Edit Card Type</h3>

        <div style={{ marginBottom: "10px" }}>
          <label>Current Background:</label><br />
          <img
            src={previewUrl}
            alt="Current Background"
            style={{ width: 100, height: 100, objectFit: 'cover' }}
          />
        </div>

        <div style={{ marginBottom: "10px" }}>
          <label>Upload New Background (PNG):</label><br />
          <input
            type="file"
            accept="image/*"
            onChange={(e) => setNewBackgroundFile(e.target.files[0])}
            style={{ marginLeft: "10px" }}
          />
        </div>

        <div style={{ marginBottom: "10px" }}>
          <label>Symbol:</label><br />
          <select
            value={symbolId}
            onChange={(e) => setSymbolId(e.target.value)}
            style={{ width: "400px", marginLeft: "10px" }}
          >
            <option value="">No symbol</option>
            {symbols.filter(symbol => symbol.symbol_type === "Card type").sort((a, b) => ((a.translations?.en?.name || a.translations?.en) || `Symbol ${a.symbol_id}`).localeCompare((b.translations?.en?.name || b.translations?.en) || `Symbol ${b.symbol_id}`)).map(symbol => (
              <option key={symbol.symbol_id} value={symbol.symbol_id}>
                {(symbol.translations?.en?.name || symbol.translations?.en) || `Symbol ${symbol.symbol_id}`} ({symbol.symbol_type})
              </option>
            ))}
          </select>
        </div>

        <div style={{ marginBottom: "10px" }}>
          <label>Translations:</label>
          {languages.map(lang => (
            <div key={lang.code} style={{ marginTop: "10px" }}>
              <h4>{lang.name} ({lang.code})</h4>
              <div style={{ marginLeft: "20px" }}>
                <div style={{ marginBottom: "5px" }}>
                  <label>Name:</label>
                  <input
                    type="text"
                    value={translations[lang.code]?.name || ""}
                    onChange={(e) => setTranslations(prev => ({
                      ...prev,
                      [lang.code]: {
                        ...prev[lang.code],
                        name: e.target.value
                      }
                    }))}
                    style={{ width: "400px", marginLeft: "10px" }}
                  />
                </div>
              </div>
            </div>
          ))}
        </div>

        <button type="submit" disabled={uploading}>Save Card Type</button>
        <button type="button" onClick={onCancel} style={{ marginLeft: "10px" }}>Cancel</button>
      </form>
    </div>
  );
}

export { CardTypesTab };